import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { ConfirmModal } from "@solomon-apps/shared/src/components/ui/confirm-modal";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { Input, UploadFiles } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useMemo, useState } from "react";

import {
    type BeneficialOwnershipEntry,
    type BeneficialOwnershipPayload,
    submitBeneficialOwnership
} from "@/services/clientOnboarding";
import { uploadOnboardingDocument } from "@/services/clientOnboardingDocuments";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import { usePersonDetailFormState } from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { payloadIsDirty } from "../panel/payloadIsDirty";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "../panel/uploadConstraints";

const UBO_ID_DOC_EXAMPLES: string[] = [
    "Passport",
    "National ID card",
    "Driver's license"
];

/**
 * Per-UBO beneficiary form. Each UBO is backed by an uploaded
 * government photo ID — the doc is the source-of-truth for legal
 * name, date of birth, and nationality. Solomon's review extracts
 * those values from the doc and matches them against the typed
 * legal name (which serves as a working label for the list). The
 * earlier 4-step Persona-style biometric KYC remains deferred to
 * v2+; the doc upload is the institutional minimum until then.
 */
export function PersonDetail({
    personLabel,
    legalName,
    onLegalNameChange,
    entryUuid,
    onSubmit,
    onDelete
}: {
    personLabel: string;
    /** Controlled — lives in the hub so the typed name can feed back
     *  into the synthetic slot title in the left list. */
    legalName: string;
    onLegalNameChange: (next: string) => void;
    /** Stable per-UBO id, generated when the entry was added or
     *  hydrated from BE. Used as the row identity for both the doc
     *  upload (entryUuid filter) and the entries[] payload. */
    entryUuid?: string;
    onSubmit: (payload: unknown) => void;
    onDelete?: () => void;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    const persistedStep = context?.stepsByName.beneficial_ownership;

    // Per-UBO field values + lastSubmitted snapshot live in the
    // hub-scoped form-state context, keyed by entryUuid, so edits survive
    // navigation between beneficiaries / other steps. Server hydration for
    // an existing entry is handled inside usePersonDetailFormState.
    const [personState, setPersonState] = usePersonDetailFormState(entryUuid);
    const { ownershipPct, role, idDocumentUuid, idFilename, lastSubmitted } =
        personState;
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    // Validation: trim name, parse ownership as a real number in
    // (0, 100], and require the ID upload. Closes the medium audit
    // finding on PersonDetail accepting whitespace-only names and
    // "0" / non-numeric ownership.
    const ownershipNum = parseFloat(ownershipPct);
    const idAttached = idDocumentUuid !== null;
    const submittable =
        legalName.trim().length > 0 &&
        !Number.isNaN(ownershipNum) &&
        ownershipNum > 0 &&
        ownershipNum <= 100 &&
        idAttached;

    const handleIdSelect = async (files: FileList | null): Promise<void> => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!context || !entryUuid) {
            // Dev/mock path — no live application; surface the file name
            // optimistically.
            setPersonState((prev) => ({
                ...prev,
                idDocumentUuid: `mock-${Date.now()}`,
                idFilename: file.name
            }));
            return;
        }
        setUploading(true);
        setSubmitError(null);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid: context.application.uuid,
                purpose: "government_id",
                entryUuid,
                file
            });
            setPersonState((prev) => ({
                ...prev,
                idDocumentUuid: doc.uuid,
                idFilename: doc.filename
            }));
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "ID document upload failed. Choose the file again and retry."
                )
            );
        } finally {
            setUploading(false);
        }
    };

    const buildEntries = (
        thisEntry: BeneficialOwnershipEntry
    ): BeneficialOwnershipEntry[] => {
        const existing =
            (
                persistedStep?.payload as
                    | Partial<BeneficialOwnershipPayload>
                    | undefined
            )?.entries ?? [];
        const idx = existing.findIndex(
            (e) => e.entryUuid === thisEntry.entryUuid
        );
        if (idx === -1) return [...existing, thisEntry];
        const next = existing.slice();
        next[idx] = thisEntry;
        return next;
    };

    const currentSnapshot = useMemo<{
        legalName: string;
        ownershipPct: number;
        idDocumentUuid: string;
        role: string | null;
    } | null>(() => {
        if (!submittable || !idDocumentUuid) return null;
        return {
            legalName: legalName.trim(),
            ownershipPct: ownershipNum,
            idDocumentUuid,
            role: role.trim() || null
        };
    }, [submittable, legalName, ownershipNum, idDocumentUuid, role]);

    const isDirty =
        lastSubmitted !== null &&
        payloadIsDirty(currentSnapshot, lastSubmitted);
    const hasPartialInput =
        legalName.trim().length > 0 ||
        ownershipPct.trim().length > 0 ||
        role.trim().length > 0 ||
        idDocumentUuid !== null;
    const hasDraft = isDirty || (lastSubmitted === null && hasPartialInput);
    useReportOnboardingDraft("beneficial-ownership", hasDraft);

    const handleSave = async (): Promise<void> => {
        if (!submittable || !entryUuid || !idDocumentUuid || !currentSnapshot)
            return;
        const thisEntry: BeneficialOwnershipEntry = {
            entryUuid,
            legalName: legalName.trim(),
            ownershipPercent: ownershipNum,
            governmentIdUuid: idDocumentUuid,
            role: role.trim() || null,
            entryStatus: "complete"
        };

        const payload: BeneficialOwnershipPayload = {
            entries: buildEntries(thisEntry)
        };

        if (!context) {
            onSubmit(payload);
            setPersonState((prev) => ({
                ...prev,
                lastSubmitted: currentSnapshot
            }));
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await submitBeneficialOwnership(
                context.application.uuid,
                payload
            );
            context.applyStepUpdate(result);
            onSubmit(payload);
            setPersonState((prev) => ({
                ...prev,
                lastSubmitted: currentSnapshot
            }));
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Beneficial owner details were not saved. Complete the required fields and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (): Promise<void> => {
        if (!entryUuid) {
            onDelete?.();
            return;
        }
        if (!context) {
            onDelete?.();
            return;
        }
        // Post the entries[] array WITHOUT this entry.
        const existing =
            (
                persistedStep?.payload as
                    | Partial<BeneficialOwnershipPayload>
                    | undefined
            )?.entries ?? [];
        const remaining = existing.filter((e) => e.entryUuid !== entryUuid);
        try {
            const result = await submitBeneficialOwnership(
                context.application.uuid,
                { entries: remaining }
            );
            context.applyStepUpdate(result);
        } catch (err) {
            // Surface the error but still let the local state update —
            // the BE call is the source of truth, but the user clicked
            // Delete so we shouldn't leave them stuck in the modal.
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Beneficial owner was not removed. Refresh onboarding and try again."
                )
            );
        }
        onDelete?.();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={personLabel}
                actions={
                    onDelete ? (
                        <Button
                            variant="sunken"
                            interaction="lifted"
                            outline
                            size="sm"
                            onClick={() => setDeleteOpen(true)}
                        >
                            Delete
                        </Button>
                    ) : undefined
                }
                className="pb-4 border-b border-line-base"
            />
            {onDelete && (
                <ConfirmModal
                    open={deleteOpen}
                    onOpenChange={setDeleteOpen}
                    titleVariant="h2"
                    title={`Delete ${personLabel}?`}
                    description="This removes the beneficiary. You can add a replacement at any time."
                    confirmLabel="Delete"
                    destructive
                    onConfirm={() => void handleDelete()}
                />
            )}

            <FormCard
                title="Legal Name"
                hint="Full legal name as it appears on your government photo ID."
            >
                <Input
                    aria-label="Legal Name"
                    value={legalName}
                    onChange={(e) => onLegalNameChange(e.target.value)}
                    placeholder="e.g. Sarah Chen"
                />
            </FormCard>

            <FormCard
                title="Ownership Percentage"
                hint="Ownership percentage of the entity."
            >
                <Input
                    aria-label="Ownership Percentage"
                    type="number"
                    min={0}
                    max={100}
                    value={ownershipPct}
                    onChange={(e) =>
                        setPersonState((prev) => ({
                            ...prev,
                            ownershipPct: e.target.value
                        }))
                    }
                    placeholder="e.g. 35"
                    className="w-24"
                />
            </FormCard>

            <FormCard
                title="Role (Optional)"
                hint="Their role at the entity. Founder, CEO, Director, etc."
            >
                <Input
                    aria-label="Role (Optional)"
                    value={role}
                    onChange={(e) =>
                        setPersonState((prev) => ({
                            ...prev,
                            role: e.target.value
                        }))
                    }
                    placeholder="e.g. Co-founder"
                />
            </FormCard>

            {/* Not a FormCard: its Field applies one uniform gap to every
                child, and this block needs two — the heading bound tightly
                to the examples card, then the uploader set apart from both. */}
            <div className="pt-6 flex w-full flex-col gap-8">
                <div className="flex flex-col gap-2">
                    <Text variant="h3" render={<span />}>
                        Government Photo ID
                    </Text>
                    <Card surface="sunken" border={false} className="px-5 py-4">
                        <Text
                            variant="label"
                            className="block text-foreground-neutral pb-2"
                        >
                            Examples of Accepted Documents
                        </Text>
                        <ul className="space-y-1.5">
                            {UBO_ID_DOC_EXAMPLES.map((doc) => (
                                <li
                                    key={doc}
                                    className="flex items-center gap-2"
                                >
                                    <IndicationUnit
                                        variant="status"
                                        state="disabled"
                                        size={6}
                                    />
                                    <Text
                                        variant="small"
                                        className="leading-none text-foreground-base"
                                    >
                                        {doc}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>

                <div>
                    <UploadFiles
                        className="max-w-sm mx-auto"
                        size="sm"
                        accept={DOCUMENT_ACCEPT}
                        maxSize={DOCUMENT_MAX_SIZE}
                        state={
                            uploading
                                ? "uploading"
                                : idAttached
                                  ? "success"
                                  : "empty"
                        }
                        filename={idFilename ?? undefined}
                        onSelect={(files) => void handleIdSelect(files)}
                    />
                </div>
            </div>

            <div className="pt-12 max-w-sm mx-auto">
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="sm"
                    fullWidth
                    disabled={
                        !submittable ||
                        submitting ||
                        uploading ||
                        (lastSubmitted !== null && !isDirty)
                    }
                    onClick={() => void handleSave()}
                >
                    {submitting
                        ? "Saving…"
                        : lastSubmitted !== null && !isDirty
                          ? "Saved"
                          : "Save Beneficiary"}
                </Button>
                {submitError && (
                    <Text
                        variant="small"
                        className="block leading-snug text-error-base text-center pt-3"
                    >
                        {submitError}
                    </Text>
                )}
            </div>
        </motion.div>
    );
}
